import React, { useLayoutEffect } from "react";
// import { useTranslation } from "react-i18next";
import SplitType from "split-type";
import gsap from "gsap";
import OtherProjects from "../../components/Other-Projects/otherProjects";
import ProjectNav from "../../components/Project-Nav/projectNav";

export default function CRMProject() {
  // const { t } = useTranslation();

  useLayoutEffect(() => {
    const text = new SplitType(".words1", { types: "chars" });
    const textA = SplitType.create(".sentence1", { types: "words" });
    const textB = SplitType.create(".line1", { types: "lines" });

    gsap.fromTo(
      text.chars,
      { y: 0, opacity: 0 },
      { y: -12, opacity: 1, stagger: 0.03 }
    );
    gsap.fromTo(
      textA.words,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.06 }
    );
    gsap.fromTo(
      textB.lines,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.1 }
    );

    gsap.fromTo(
      ".box1",
      { y: 0, opacity: 0, delay: 1 },
      { y: -20, opacity: 2, duration: 1 }
    );

    gsap.config({
      force3D: false,
      nullTargetWarn: false,
      trialWarn: false,
    });
  });

  return (
    <div className="project-section case-study crm-project container mt-120">
      <div className="project-hero-card bg-secondary radius-20 w-100 p-40 sm:p-20 xs:p-20 box1">
        <div className="project-hero-title mb-20 sm:mb-12 xs:mb-8">
          <h1 className="f-40 xs:f-18 xs:l-h-24 sm:f-32 l-h-40 f-w-600 text-primary words1">
            Maxis CRM
          </h1>
          <p className="f-16 xs:f-12 xs:l-h-20 sm:f-14 l-h-30 f-w-300 text-primary mt-8 sentence1">
            <span>B2B CRM · Sales &amp; Marketing · Calibraint Technologies</span>
          </p>
        </div>
      </div>

      <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mt-24 mb-20 mw-680 line1">
        This was one of those projects where the challenge wasn't building
        features — it was figuring out what the sales team actually needed
        day to day. Designed the full CRM from research to handoff, covering
        pipeline management, lead tracking, campaign module, and task
        scheduling. Benchmarked against HubSpot, Freshdesk, and Zoho, and
        used Zeplin for developer handoff.
      </p>

      <div className="cs-pills">
        <span className="cs-pill">B2B SaaS</span>
        <span className="cs-pill">Figma + Zeplin</span>
        <span className="cs-pill">4 Core Modules</span>
        <span className="cs-pill">Calibraint Technologies</span>
      </div>

      <div className="cs-stats">
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#38bdf8" }}>4</div>
          <div className="cs-stat-l">Core modules</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#a78bfa" }}>3</div>
          <div className="cs-stat-l">CRMs benchmarked</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#fb923c" }}>1</div>
          <div className="cs-stat-l">Solo designer</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#4ade80" }}>✓</div>
          <div className="cs-stat-l">Live with sales team</div>
        </div>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">The problem</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          Sales teams were managing customers across spreadsheets, email
          threads, and an old legacy tool. There was no central place to
          track pipeline stages, log calls, or see what the team was working
          on. Things were constantly falling through the gaps — and nobody
          had a clear view of where any deal actually stood.
        </p>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          The product manager came in with a big list of features. My job
          was to work out what the sales reps actually needed first, not
          just what the business wanted to build.
        </p>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 crm-cover-2"></div>

      <div className="mt-40 mb-32">
        <div className="cs-lbl">My role</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          I was the only designer on this. Handled everything — stakeholder
          interviews, competitive research, information architecture,
          wireframes, high-fidelity designs in Figma, and the final Zeplin
          handoff. Worked closely with the PM to decide what to prioritise
          and what to push to later phases.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Research</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          What I found talking to the sales team
        </h3>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          Spent time going through HubSpot, Freshdesk, and Zoho — not to
          copy them, but to understand what patterns sales reps already
          knew. There's no point reinventing how a pipeline board works
          when users have muscle memory from existing tools. The
          differentiation had to come from how things were connected, not
          from how they looked.
        </p>
        <div className="cs-quote">
          <p>
            "The problem isn't that we don't have a CRM. It's that the one
            we have takes more time than just using WhatsApp and Excel."
          </p>
          <span>— Sales rep, stakeholder interview</span>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 crm-process-1"></div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Pain points that came up</div>
        <div className="cs-pains">
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              No single view of the pipeline — reps were manually compiling
              deal status before every team meeting, which was eating into
              actual selling time
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Leads from marketing campaigns had zero connection to the
              sales pipeline — two teams working in completely separate
              systems with no shared data
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Task follow-ups were getting missed because the task tool sat
              completely separate from the contact record — too many
              clicks, so reps just didn't bother
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              New reps took weeks to get comfortable — there was no logical
              path built into the interface itself, just a wall of features
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Team members couldn't see each other's notes on shared
              accounts — duplicate outreach to the same contact was a
              regular complaint
            </div>
          </div>
        </div>
        <div className="cs-quote">
          <p>
            The real insight: sales reps don't need more features, they need
            fewer clicks. Every extra step between them and their next
            action is a lead they won't follow up on.
          </p>
        </div>
      </div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 crm-process-2"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 crm-process-3"></div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Design decisions</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          What mattered and why
        </h3>
        <div className="cs-two-col">
          <div className="cs-card box1">
            <h4>Pipeline front and centre</h4>
            <p>
              Made the pipeline the homepage — not a summary feed, not a
              widget. You open the app and the first thing you see is where
              every deal stands and what needs attention today.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Campaigns connect to leads</h4>
            <p>
              When a lead comes in from a campaign, it auto-populates the
              sales pipeline with the source tagged. Both teams finally
              seeing the same data.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Tasks live inside contacts</h4>
            <p>
              Removed the separate task module. Call logs and follow-ups
              live directly inside the contact record. That single change
              got the most positive mention after launch.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Component library first</h4>
            <p>
              Built a shared component library in Figma — badges, table
              rows, form fields, cards — so any new module added later
              wouldn't need design work from scratch.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 crm-img2"></div>
      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 crm-img3"></div>
      <div className="cs-img bg-secondary radius-20 minh-680 sm:minh-450 xs:minh-200 mt-32 box1 crm-img4"></div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 crm-img5"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 crm-img1"></div>
      </div>

      <div className="mt-32 mb-32">
        <div className="cs-lbl">Handoff</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          Used Zeplin for the developer handoff. Documented every component
          state — default, hover, active, error, empty. The sales team
          adopted the platform after the build. That was the best signal
          the design decisions translated well into the actual product.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Outcomes</div>
        <div className="cs-outcomes">
          <div className="cs-outcome">
            <div className="ico">📦</div>
            <h4>4 modules shipped</h4>
            <p>
              Pipeline, Leads, Campaign Tracking, Task Scheduling —
              connected through one shared data layer.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🔍</div>
            <h4>Competitive gaps found</h4>
            <p>
              2 areas where Maxis could genuinely differentiate from HubSpot
              and Zoho rather than just matching them.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">✅</div>
            <h4>Live with the team</h4>
            <p>
              Platform went live and was picked up by the sales and
              marketing team — real users, real adoption.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🧩</div>
            <h4>Design system built</h4>
            <p>
              Component library covering all states — used as the base for
              anything added to the product after launch.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🗺️</div>
            <h4>IA mapped first</h4>
            <p>
              Full site map done before any screens — saved several rounds
              of design rework mid-build.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">⚡</div>
            <h4>Solo end-to-end</h4>
            <p>
              Research, IA, wireframes, high-fidelity, handoff — one
              designer, start to finish.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-32">
        <div className="cs-lbl">Reflection</div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>CRM design is mostly about what to cut.</strong> The
            temptation is to add every feature someone mentions. The harder
            job is deciding what not to show — and in what order the
            information appears based on what a rep actually does during
            their day.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Competitive research isn't about copying.</strong> Going
            through HubSpot and Zoho taught me which patterns users already
            know — so I could lean into familiarity where it helped, and
            break convention only where there was a real reason to.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Zeplin changed how I thought about design states.</strong>{" "}
            Knowing devs would reference every state made me more careful
            about edge cases — empty states, errors, loading — well before
            any file left my hands.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>If I did this again:</strong> Push for user testing with
            actual sales reps earlier — not just stakeholder reviews.
            Stakeholders represent what the business wants. Reps know what's
            painful day-to-day.
          </p>
        </div>
      </div>

      <div className="project-bottom-nav mt-60 mb-100 flex justify-between align-center box1">
        <div className="flex xs:justify-center">
          <OtherProjects active="crm" />
        </div>
        <ProjectNav current="crm" />
      </div>
    </div>
  );
}
